import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import { safeJsonLd } from "./utils/safeJsonLd";
import {
  ArrowRight, Menu, X, Heart, Users, Baby, Stethoscope, Leaf,
  Apple, Shield, Globe, ChevronDown, Phone, Mail, Clock,
  Sparkles, Target, UserCheck, MessageCircle, Activity,
  Facebook, Instagram, Youtube
} from "lucide-react";

/* ─── Reusable animation wrapper (matches existing shared.js) ─── */
const Anim = ({ children, className = "", delay = 0 }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.65, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}>{children}</motion.div>
  );
};

/* ─── Constants ─── */
const WHATSAPP_PHONE = "919108818369";

/* ─── Section heading component ─── */
const SectionHeading = ({ eyebrow, heading, children, light = false }) => (
  <Anim>
    <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
      {eyebrow && <span className={`text-xs tracking-[0.2em] uppercase font-semibold ${light ? "text-accent" : "text-accent"}`}>{eyebrow}</span>}
      <h2 className={`font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight mt-3 ${light ? "text-bone" : "text-emerald_deep"}`}>{heading}</h2>
      {children && <div className={`mt-4 text-base leading-relaxed max-w-2xl mx-auto ${light ? "text-bone/70" : "text-text_muted"}`}>{children}</div>}
    </div>
  </Anim>
);

/* ─── Card component ─── */
const Card = ({ icon: Icon, title, children, iconColor = "text-accent" }) => (
  <div className="bg-surface border border-border_custom p-6 md:p-8 rounded-xl hover:shadow-md transition-shadow">
    {Icon && <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4"><Icon size={20} className={iconColor} /></div>}
    <h3 className="font-serif text-lg md:text-xl text-emerald_deep mb-3">{title}</h3>
    <div className="text-sm text-text_muted leading-relaxed">{children}</div>
  </div>
);

/* ─── FAQ Item ─── */
const FAQItem = ({ q, a, open, onToggle }) => (
  <div className="border-b border-border_custom">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-5 text-left group" aria-expanded={open}>
      <span className="font-serif text-base md:text-lg text-emerald_deep pr-4 group-hover:text-accent transition-colors">{q}</span>
      <ChevronDown size={18} className={`text-text_muted flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-accent" : ""}`} />
    </button>
    <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
      transition={{ duration: 0.3 }} className="overflow-hidden">
      <p className="text-sm text-text_muted leading-relaxed pb-5">{a}</p>
    </motion.div>
  </div>
);

/* ─── Page Header (visually matches existing, works with href links) ─── */
const LandingHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const navItems = [
    { label: "Home", href: "/" },
    { label: "Programs", href: "/#programs" },
    { label: "Team", href: "/#team" },
    { label: "Testimonials", href: "/#testimonials" },
    { label: "Contact", href: "/#contact" }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-bone/95 backdrop-blur-xl shadow-sm" : "bg-transparent"}`}
      data-testid="landing-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="font-serif text-2xl tracking-tight">
            <span className={`font-semibold ${scrolled ? "text-emerald_deep" : "text-bone"}`}>Fitly</span>
            <span className={`font-light ml-1 ${scrolled ? "text-text_muted" : "text-bone/60"}`}>India</span>
          </a>
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navItems.map(item => (
              <a key={item.label} href={item.href}
                className={`hover:text-accent transition-colors text-sm tracking-wide ${scrolled ? "text-text_muted" : "text-bone/70"}`}>{item.label}</a>
            ))}
          </nav>
          <div className="hidden md:block">
            <a href="/consultation" className="bg-accent text-emerald_deep px-7 py-2.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all inline-flex items-center gap-2 rounded-full">
              Book Consultation <ArrowRight size={14} />
            </a>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className={`md:hidden ${scrolled ? "text-emerald_deep" : "text-bone"}`}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden py-6 bg-surface border border-border_custom rounded-xl mt-2 mb-4 shadow-lg">
            {navItems.map(item => (
              <a key={item.label} href={item.href}
                className="block w-full text-left px-6 py-3 text-text_muted hover:text-emerald_deep hover:bg-bone/50 text-sm tracking-wide">{item.label}</a>
            ))}
            <div className="px-6 pt-3">
              <a href="/consultation" className="block w-full bg-emerald_deep text-bone py-3 text-sm tracking-wide text-center rounded-full">Book Consultation</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

/* ─── Page Footer (matches existing) ─── */
const LandingFooter = () => (
  <footer className="bg-emerald_deep text-bone py-16 relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div className="md:col-span-2">
          <div className="font-serif text-3xl mb-4"><span className="text-accent">Fitly</span> India</div>
          <p className="text-bone/60 leading-relaxed max-w-sm">Fertility Nutrition & Conception Care. Personalised online nutrition for women, men and couples across India and internationally.</p>
          <div className="flex gap-4 mt-6">
            {[[Facebook, "https://www.facebook.com/294056110466070"], [Instagram, "https://www.instagram.com/fitly_india"], [Youtube, "https://www.youtube.com/fitly_india"]].map(([Icon, href]) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-bone/10 flex items-center justify-center hover:bg-accent/20 transition-colors"><Icon size={16} className="text-bone/70" /></a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Quick Links</h4>
          <ul className="space-y-2">
            {[["Home", "/"], ["Programs", "/#programs"], ["Team", "/#team"], ["Testimonials", "/#testimonials"], ["Consultation", "/consultation"], ["Fertility Nutrition", "/fertility-nutrition"], ["Female Fertility", "/female-fertility-nutrition"], ["Male Fertility", "/male-fertility-nutrition"]].map(([label, href]) => (
              <li key={label}><a href={href} className="text-bone/60 hover:text-accent transition-colors text-sm">{label}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-bone/60">
            <li><a href="tel:+919108818369" className="hover:text-accent transition-colors">+91 91088 18369</a></li>
            <li><a href="mailto:fitlyindiahealth@gmail.com" className="hover:text-accent transition-colors">fitlyindiahealth@gmail.com</a></li>
            <li>Mon - Sat, 10 AM - 6 PM</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-bone/10 pt-8 text-center text-bone/40 text-xs">
        &copy; {new Date().getFullYear()} Fitly India. All rights reserved.
      </div>
    </div>
  </footer>
);

/* ═══════════════════════════════════════════════════════════
   STRUCTURED DATA
   ═══════════════════════════════════════════════════════════ */

const faqData = [
  { q: "What is a fertility nutritionist?", a: "A fertility nutritionist is a nutrition professional who specialises in supporting reproductive health through personalised dietary guidance. They work with individuals and couples to address nutritional factors that can influence overall preconception health, menstrual regularity, egg and sperm quality, metabolic health and readiness for conception or fertility treatment." },
  { q: "Can nutrition help improve fertility?", a: "Nutrition plays a recognised role in supporting overall reproductive health. Correcting nutritional deficiencies, managing metabolic conditions like insulin resistance, achieving a healthy weight, and improving dietary patterns can positively influence menstrual regularity and general preconception health. However, nutrition alone cannot resolve all causes of infertility, and medical evaluation is recommended when needed." },
  { q: "Can diet improve egg quality?", a: "A nutrient-dense diet rich in antioxidants, healthy fats, adequate protein and essential micronutrients can support the cellular environment in which eggs develop over approximately 90 days before ovulation. While no food can reverse age-related ovarian decline, optimising nutrition is a reasonable and evidence-informed step toward supporting overall reproductive health." },
  { q: "Can food increase AMH?", a: "AMH (Anti-Mullerian Hormone) is primarily a marker of ovarian reserve and reflects the number of remaining follicles. No food, supplement or diet has been proven to increase AMH levels. Fitly focuses on overall nutritional and reproductive health rather than making AMH-specific promises. Medical evaluation by a fertility specialist is always recommended for concerns about ovarian reserve." },
  { q: "Do you provide nutrition for PCOS and fertility?", a: "Yes. Fitly provides personalised nutrition support for women with PCOS who are trying to conceive. The focus is on supporting metabolic health, insulin sensitivity, nutritional adequacy and sustainable lifestyle changes alongside any ongoing medical care." },
  { q: "Does Fitly provide male fertility nutrition?", a: "Yes. Male reproductive health deserves the same attention as female reproductive health. Fitly provides nutrition and lifestyle support for men with concerns involving sperm parameters, weight management, nutritional deficiencies and preconception health." },
  { q: "Can husband and wife join the program together?", a: "Absolutely. Fitly offers couple-focused fertility nutrition programs where both partners receive personalised nutrition strategies based on their individual health profiles, while working toward a shared preconception goal. Couple programs are available across all Fitly tiers." },
  { q: "When should we start fertility nutrition before trying to conceive?", a: "Ideally, preconception nutrition should begin at least three to six months before trying to conceive. This allows time to address nutritional deficiencies, improve dietary habits, and support the approximately 90-day egg and sperm development cycles. However, it is never too late to start making beneficial changes." },
  { q: "Can I join Fitly from outside Bangalore?", a: "Yes. Fitly's online fertility nutrition programs are available for clients across India and internationally. Consultations are conducted via video or voice call, and personalised plans are delivered digitally with ongoing support." },
  { q: "Should I stop fertility treatment while following a fertility nutrition program?", a: "No. Fitly's nutrition support is designed to work alongside — not in place of — medical fertility treatment. You should continue following your treating fertility specialist's guidance. Nutrition and lifestyle improvements complement medical care; they do not replace it." },
  { q: "What should I eat before IVF?", a: "Before IVF, focus on a balanced, nutrient-rich diet that includes adequate protein, healthy fats, complex carbohydrates, antioxidant-rich fruits and vegetables, and essential micronutrients including folate, iron, vitamin D and omega-3 fatty acids. Fitly creates personalised pre-IVF nutrition plans tailored to individual needs, health history and relevant reports." },
  { q: "How does Fitly's fertility nutrition program work?", a: "Fitly begins with an initial consultation to understand your health history, fertility journey, dietary patterns and goals. Relevant reports are reviewed. A personalised nutrition strategy is developed — not a generic diet chart. Progress is followed through the program with appropriate nutritional adjustments, and medical referral is encouraged whenever something requires clinical rather than nutritional management." }
];

const structuredData = {
  faq: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  },
  breadcrumb: {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fitlyindia.org/" },
      { "@type": "ListItem", "position": 2, "name": "Fertility Nutrition", "item": "https://fitlyindia.org/fertility-nutrition/" }
    ]
  },
  service: {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Online Fertility Nutrition Consultation",
    "provider": {
      "@type": "Organization",
      "name": "Fitly India",
      "url": "https://fitlyindia.org",
      "telephone": "+919108818369",
      "email": "fitlyindiahealth@gmail.com",
      "address": { "@type": "PostalAddress", "addressLocality": "Bangalore", "addressCountry": "IN" },
      "sameAs": ["https://www.facebook.com/294056110466070", "https://www.instagram.com/fitly_india", "https://www.youtube.com/fitly_india"]
    },
    "serviceType": "Fertility Nutrition Consultation",
    "areaServed": { "@type": "Country", "name": "India" },
    "description": "Personalised online fertility nutrition for women, men and couples. Support for preconception health, PCOS, egg health, male fertility and IVF preparation.",
    "availableChannel": { "@type": "ServiceChannel", "serviceType": "Online", "serviceUrl": "https://fitlyindia.org/consultation" },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Fertility Nutrition Programs",
      "itemListElement": [
        { "@type": "Offer", "name": "Voice Call Consultation", "price": "1250", "priceCurrency": "INR", "description": "30-minute online voice consultation" },
        { "@type": "Offer", "name": "Video Call Consultation", "price": "2250", "priceCurrency": "INR", "description": "30-minute online video consultation" }
      ]
    }
  },
  persons: [
    { "@context": "https://schema.org", "@type": "Person", "name": "Mrs. Nadiya", "jobTitle": "Deep Health Coach, Precision Nutrition Level 2 Certified", "worksFor": { "@type": "Organization", "name": "Fitly India" } },
    { "@context": "https://schema.org", "@type": "Person", "name": "Mrs. Revathy R.P", "jobTitle": "Clinical Nutritionist, Certified in Fertility Nutrition", "worksFor": { "@type": "Organization", "name": "Fitly India" } }
  ]
};


/* ═══════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════════════════ */

export default function FertilityNutritionPage() {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-bone text-text_main" data-testid="fertility-nutrition-page">
      <Helmet>
        <title>Online Fertility Nutrition Consultation in India | Fitly India</title>
        <meta name="description" content="Personalised online fertility nutrition for women, men and couples. Support for preconception, PCOS, egg health, male fertility and IVF preparation from Fitly India." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://fitlyindia.org/fertility-nutrition/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fitlyindia.org/fertility-nutrition/" />
        <meta property="og:title" content="Online Fertility Nutrition Consultation in India | Fitly India" />
        <meta property="og:description" content="Personalised online fertility nutrition for women, men and couples. Support for preconception, PCOS, egg health, male fertility and IVF preparation from Fitly India." />
        <meta property="og:site_name" content="Fitly India" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Online Fertility Nutrition Consultation in India | Fitly India" />
        <meta name="twitter:description" content="Personalised online fertility nutrition for women, men and couples. Preconception, PCOS, egg health, male fertility and IVF preparation support." />
      </Helmet>

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.faq) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.service) }} />
      {structuredData.persons.map((p, i) => (
        <script key={`person-${i}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(p) }} />
      ))}

      <LandingHeader />

      <main>
        {/* ═══ HERO ═══ */}
        <section className="relative min-h-screen flex items-center bg-emerald_deep pt-20 overflow-hidden" data-testid="fn-hero">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 20%, #D4AF37 1px, transparent 1px)", backgroundSize: "60px 60px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10 py-16 md:py-24">
            <div className="max-w-3xl">
              <Anim>
                <nav aria-label="Breadcrumb" className="mb-8">
                  <ol className="flex items-center gap-2 text-xs text-bone/40">
                    <li><a href="/" className="hover:text-accent transition-colors">Home</a></li>
                    <li>/</li>
                    <li className="text-accent">Fertility Nutrition</li>
                  </ol>
                </nav>
              </Anim>
              <Anim delay={0.1}>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-bone mb-6">
                  Online Fertility Nutrition<br />
                  <span className="italic font-light text-accent">Consultation in India</span>
                </h1>
              </Anim>
              <Anim delay={0.2}>
                <p className="text-lg md:text-xl text-bone/80 leading-relaxed mb-6 max-w-2xl">
                  Personalised Fertility Nutrition for Women, Men & Couples Trying to Conceive
                </p>
              </Anim>
              <Anim delay={0.3}>
                <p className="text-base text-bone/60 leading-relaxed mb-4">
                  Trying for a baby can quickly become a journey filled with tests, supplements, conflicting advice and uncertainty about what to eat or change.
                </p>
                <p className="text-base text-bone/60 leading-relaxed mb-4">
                  At Fitly India, fertility nutrition is not treated like a generic weight-loss diet. We provide personalised fertility nutrition and conception-care support for women, men and couples based on their health history, lifestyle, nutritional status, fertility concerns and relevant medical reports.
                </p>
              </Anim>
              <Anim delay={0.4}>
                <div className="flex flex-wrap gap-3 mb-10">
                  {["Trying Naturally", "Preparing for IVF", "Female Fertility", "Male Fertility", "Couple Fertility"].map(tag => (
                    <span key={tag} className="border border-accent/30 text-accent text-xs tracking-wider px-4 py-2 rounded-full">{tag}</span>
                  ))}
                </div>
              </Anim>
              <Anim delay={0.5}>
                <a href="/consultation" data-testid="hero-cta"
                  className="inline-flex items-center gap-2 bg-accent text-emerald_deep px-8 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                  Book a Fertility Consultation <ArrowRight size={16} />
                </a>
              </Anim>
            </div>
          </div>
        </section>

        {/* ═══ SECTION 2 — WHO WE SUPPORT ═══ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="fn-who-we-support">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Fertility Nutrition Support" heading="Who Can Benefit From Fertility Nutrition Support?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: Users, text: "Couples trying to conceive naturally" },
                { icon: Heart, text: "Women preparing their health before conception" },
                { icon: Stethoscope, text: "Couples preparing for IVF or fertility treatment" },
                { icon: Activity, text: "Women with PCOS who are trying to conceive" },
                { icon: Shield, text: "Women with endometriosis who are planning pregnancy" },
                { icon: Target, text: "Women concerned about low AMH or diminished ovarian reserve" },
                { icon: Clock, text: "Women with irregular cycles or ovulation concerns" },
                { icon: UserCheck, text: "Men concerned about sperm count, motility or morphology" },
                { icon: Sparkles, text: "Couples looking for structured preconception nutrition support" }
              ].map((item, idx) => (
                <Anim key={item.text} delay={idx * 0.05}>
                  <div className="flex items-start gap-4 p-5 bg-surface border border-border_custom rounded-xl hover:shadow-sm transition-shadow">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <item.icon size={18} className="text-accent" />
                    </div>
                    <p className="text-sm text-emerald_deep leading-relaxed pt-2">{item.text}</p>
                  </div>
                </Anim>
              ))}
            </div>
            <Anim delay={0.5}>
              <div className="mt-10 bg-surface_alt border border-border_custom rounded-xl p-6 max-w-3xl mx-auto">
                <p className="text-xs text-text_muted leading-relaxed text-center">
                  <strong className="text-emerald_deep">Important:</strong> Nutrition can support overall reproductive and preconception health but cannot guarantee conception or replace appropriate medical fertility treatment. Fitly encourages medical evaluation whenever clinically indicated.
                </p>
              </div>
            </Anim>
          </div>
        </section>

        {/* ═══ SECTION 3 — FEMALE FERTILITY NUTRITION ═══ */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="fn-female-fertility">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <SectionHeading eyebrow="For Her" heading="Female Fertility Nutrition" light>
              <p>Female fertility is influenced by age, underlying medical conditions, ovulation, ovarian reserve, metabolic health, nutritional status and lifestyle. Fitly provides personalised nutrition support that addresses these interconnected factors.</p>
            </SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Sparkles, title: "Egg Health & Preconception Nutrition", text: "Nutrient-dense nutrition and addressing nutritional gaps during the months leading up to conception. The approximately 90-day egg maturation window is a valuable opportunity to optimise your nutritional foundation." },
                { icon: Activity, title: "PCOS & Fertility", text: "Nutrition supporting metabolic health, insulin sensitivity, nutritional adequacy and sustainable lifestyle changes. PCOS is one of the most common causes of ovulatory difficulty, and dietary management plays a recognised role in its care." },
                { icon: Shield, title: "Low AMH & Diminished Ovarian Reserve", text: "AMH is primarily a marker of ovarian reserve and no food or diet should be promised to restore it. Fitly focuses on overall nutritional and reproductive health alongside appropriate medical fertility care." },
                { icon: Heart, title: "Endometriosis & Fertility", text: "Personalised nutrition strategies focused on adequate nourishment and overall health alongside medical care. Endometriosis management benefits from a holistic approach that includes both clinical treatment and supportive nutrition." },
                { icon: Clock, title: "Ovulation & Menstrual Health", text: "Nutrition and lifestyle support for overall menstrual and reproductive health. Regular ovulatory cycles are a key marker of reproductive wellness, and dietary factors can influence cycle regularity." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.08}>
                  <div className="bg-bone/5 border border-bone/10 p-6 md:p-8 rounded-xl hover:bg-bone/10 transition-colors h-full">
                    <card.icon size={22} className="text-accent mb-4" />
                    <h3 className="font-serif text-lg text-bone mb-3">{card.title}</h3>
                    <p className="text-sm text-bone/65 leading-relaxed">{card.text}</p>
                  </div>
                </Anim>
              ))}
            </div>
            <Anim delay={0.5}>
              <div className="text-center mt-10">
                <a href="/female-fertility-nutrition" className="inline-flex items-center gap-2 bg-accent text-emerald_deep px-8 py-3 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full" data-testid="fn-explore-female-fertility">
                  Explore Female Fertility Nutrition <ArrowRight size={14} />
                </a>
              </div>
            </Anim>
          </div>
        </section>

        {/* ═══ SECTION 4 — MALE FERTILITY NUTRITION ═══ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="fn-male-fertility">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="For Him" heading="Male Fertility Nutrition">
              <p>Fertility is a couple's journey. Male reproductive health deserves the same attention as female reproductive health.</p>
            </SectionHeading>
            <div className="max-w-4xl mx-auto">
              <Anim>
                <p className="text-base text-text_muted leading-relaxed mb-8 text-center">
                  Fitly provides nutrition and lifestyle support for men with concerns involving sperm concentration, motility, morphology, overall semen parameters, weight and metabolic health, nutritional deficiencies, and preconception lifestyle. While nutrition cannot cure male infertility, optimising nutritional and lifestyle factors can support overall reproductive health alongside medical evaluation.
                </p>
              </Anim>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
                {["Sperm Count", "Sperm Motility", "Sperm Morphology", "Metabolic Health", "Nutritional Gaps", "Weight Management", "Lifestyle Factors", "Preconception Health"].map((item, idx) => (
                  <Anim key={item} delay={idx * 0.05}>
                    <div className="text-center p-4 bg-surface border border-border_custom rounded-xl">
                      <p className="text-sm text-emerald_deep font-medium">{item}</p>
                    </div>
                  </Anim>
                ))}
              </div>
              <Anim delay={0.4}>
                <div className="text-center">
                  <a href="/male-fertility-nutrition" className="inline-flex items-center gap-2 bg-emerald_deep text-bone px-8 py-3 text-sm tracking-wide font-medium hover:bg-emerald_deep/90 transition-all rounded-full">
                    Explore Male Fertility Support <ArrowRight size={14} />
                  </a>
                </div>
              </Anim>
            </div>
          </div>
        </section>

        {/* ═══ SECTION 5 — COUPLE FERTILITY NUTRITION ═══ */}
        <section className="py-20 md:py-28 bg-surface_alt" data-testid="fn-couple-fertility">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Together" heading="Fertility Nutrition for Couples">
              <p>Fitly takes a couple-focused approach to preconception nutrition, recognising that both partners' health contributes to the fertility journey.</p>
            </SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-10">
              <Anim>
                <div className="bg-surface border border-border_custom rounded-xl p-8 h-full">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-5">
                    <Heart size={22} className="text-accent" />
                  </div>
                  <h3 className="font-serif text-2xl text-emerald_deep mb-4">For Her</h3>
                  <p className="text-sm text-text_muted leading-relaxed">
                    Menstrual and fertility history, nutrition assessment, lifestyle evaluation, relevant reports and individual nutritional requirements — all reviewed to create a personalised strategy addressing her specific preconception needs.
                  </p>
                </div>
              </Anim>
              <Anim delay={0.1}>
                <div className="bg-surface border border-border_custom rounded-xl p-8 h-full">
                  <div className="w-12 h-12 rounded-full bg-emerald_deep/10 flex items-center justify-center mb-5">
                    <UserCheck size={22} className="text-emerald_deep" />
                  </div>
                  <h3 className="font-serif text-2xl text-emerald_deep mb-4">For Him</h3>
                  <p className="text-sm text-text_muted leading-relaxed">
                    Nutrition assessment, lifestyle evaluation, metabolic health review, relevant semen-analysis findings and individual nutritional requirements — all considered to develop a personalised plan supporting his reproductive health.
                  </p>
                </div>
              </Anim>
            </div>
            <Anim delay={0.2}>
              <p className="text-center font-serif text-xl md:text-2xl text-emerald_deep max-w-2xl mx-auto">
                Two personalised strategies. One shared goal — <span className="italic text-accent">better preconception health as a couple.</span>
              </p>
            </Anim>
          </div>
        </section>

        {/* ═══ SECTION 6 — IVF NUTRITION ═══ */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="fn-ivf-nutrition">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 40% 60%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
            <SectionHeading eyebrow="IVF Preparation" heading="Nutrition Before IVF" light>
              <p>Nutrition support before fertility treatment focuses on adequate nourishment, correcting identified nutritional deficiencies, supporting metabolic health and creating sustainable eating habits.</p>
            </SectionHeading>
            <Anim>
              <div className="bg-bone/5 border border-bone/10 rounded-xl p-8 mb-10 text-left max-w-2xl mx-auto">
                <p className="text-sm text-bone/80 leading-relaxed mb-4">
                  Fitly's nutrition support works alongside — not in place of — the client's treating fertility specialist. Medication decisions, IVF protocols and medical treatment remain under the treating physician.
                </p>
                <p className="text-sm text-bone/60 leading-relaxed">
                  The goal is to help you enter your treatment cycle well-nourished, metabolically supported and with sustainable eating habits in place.
                </p>
              </div>
            </Anim>
            <Anim delay={0.1}>
              <a href="/consultation" className="inline-flex items-center gap-2 bg-accent text-emerald_deep px-8 py-3 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                Explore IVF Nutrition Support <ArrowRight size={14} />
              </a>
            </Anim>
          </div>
        </section>

        {/* ═══ SECTION 7 — WHY FITLY ═══ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="fn-why-fitly">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="The Fitly Difference" heading="Why Choose Fitly for Fertility Nutrition?" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Baby, title: "Fertility-Focused Care", text: "Programs specifically designed around preconception and reproductive health rather than generic diet templates. Every plan is built for the fertility journey." },
                { icon: Users, title: "For Women + Men", text: "Dedicated female and male fertility nutrition support. Because conception is a shared journey, and both partners' health matters." },
                { icon: Stethoscope, title: "Multidisciplinary Approach", text: "Nutrition professionals including Mrs. Nadiya (Deep Health Coach, Precision Nutrition L2), Mrs. Revathy R.P (Clinical Nutritionist, Certified in Fertility Nutrition), alongside gynaecologists and Ayurveda specialists on the Fitly team." },
                { icon: Target, title: "Personalised Nutrition", text: "Plans based on dietary preferences, lifestyle, health history, relevant reports and nutritional requirements. No two clients receive the same plan." },
                { icon: Apple, title: "Indian Food. Real Life.", text: "Practical nutrition using familiar foods rather than unnecessarily exotic fertility superfoods. Sustainable eating that fits your daily life, culture and food preferences." },
                { icon: Globe, title: "Online Support", text: "Online fertility nutrition support for clients across India and internationally. Consultations via video or voice call with ongoing digital support throughout your program." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.08}>
                  <Card icon={card.icon} title={card.title}>{card.text}</Card>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ SECTION 8 — THE FITLY APPROACH (5 Steps) ═══ */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="fn-approach">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
            <SectionHeading eyebrow="Our Process" heading="How Fertility Nutrition at Fitly Works" light />
            <div className="space-y-0">
              {[
                { num: "01", title: "Understand", text: "Understand the client's fertility journey, medical history, lifestyle, food habits and goals through an in-depth initial consultation." },
                { num: "02", title: "Assess", text: "Review relevant reports, dietary patterns, nutritional concerns and lifestyle factors to identify areas that can be improved through nutrition." },
                { num: "03", title: "Personalise", text: "Develop an individual nutrition strategy rather than using a standard fertility diet chart. Every plan reflects the client's unique health profile." },
                { num: "04", title: "Support", text: "Follow progress and make appropriate nutritional adjustments during the program. Ongoing guidance ensures the plan evolves with the client's journey." },
                { num: "05", title: "Collaborate", text: "Encourage appropriate medical evaluation whenever something requires medical rather than nutritional management. Nutrition complements — it does not replace — clinical care." }
              ].map((step, idx) => (
                <Anim key={step.num} delay={idx * 0.1}>
                  <div className="flex gap-6 md:gap-10 py-8 border-b border-bone/10 group">
                    <div className="flex-shrink-0">
                      <span className="font-serif text-4xl md:text-5xl text-accent/30 group-hover:text-accent transition-colors">{step.num}</span>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl text-bone mb-2 group-hover:text-accent transition-colors">{step.title}</h3>
                      <p className="text-sm text-bone/60 leading-relaxed max-w-xl">{step.text}</p>
                    </div>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ SECTION 9 — TRUST SECTION ═══ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="fn-trust">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <Anim>
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Medical Responsibility</span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-emerald_deep mt-3 mb-8">
                Fertility Nutrition Is Not a <span className="italic font-light">Miracle Diet</span>
              </h2>
            </Anim>
            <Anim delay={0.1}>
              <div className="space-y-5 text-base text-text_muted leading-relaxed text-left">
                <p>There is no single food, supplement, seed, juice or fertility diet that can guarantee pregnancy.</p>
                <p>Infertility can have many causes, and some require medical evaluation and treatment.</p>
                <p>Nutrition should therefore be viewed as one component of comprehensive preconception and fertility care — not a replacement for appropriate medical treatment.</p>
                <p>Fitly's goal is to help clients improve the nutritional and lifestyle factors realistically within their control while complementing appropriate medical care.</p>
              </div>
            </Anim>
            <Anim delay={0.2}>
              <div className="gold-divider w-32 mx-auto mt-10"></div>
            </Anim>
          </div>
        </section>

        {/* ═══ SECTION 10 — FAQ ═══ */}
        <section className="py-20 md:py-28 bg-surface_alt" data-testid="fn-faq">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Common Questions" heading="Fertility Nutrition FAQ" />
            <div className="bg-surface border border-border_custom rounded-xl p-6 md:p-8">
              {faqData.map((faq, idx) => (
                <FAQItem key={faq.q} q={faq.q} a={faq.a} open={openFaq === idx}
                  onToggle={() => setOpenFaq(openFaq === idx ? null : idx)} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══ FINAL CTA ═══ */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="fn-final-cta">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 60% 40%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
            <Anim>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-bone mb-6">
                Trying to Conceive?<br />
                <span className="italic font-light text-accent">Start With Your Health.</span>
              </h2>
            </Anim>
            <Anim delay={0.1}>
              <p className="text-base text-bone/70 leading-relaxed max-w-2xl mx-auto mb-10">
                Whether you're beginning your conception journey, have been trying for some time, are preparing for fertility treatment, or want to work on male and female reproductive health together, Fitly can help you build a personalised nutrition strategy for the journey ahead.
              </p>
            </Anim>
            <Anim delay={0.2}>
              <div className="mb-10">
                <p className="font-serif text-2xl text-bone mb-1"><span className="text-accent">Fitly</span> India</p>
                <p className="text-sm text-bone/60 tracking-wider uppercase mb-1">Fertility Nutrition & Conception Care</p>
                <p className="text-xs text-accent tracking-wider">For Her. For Him. For Couples.</p>
              </div>
            </Anim>
            <Anim delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/consultation" data-testid="final-cta-book"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-emerald_deep px-10 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                  Book Your Fertility Consultation <ArrowRight size={16} />
                </a>
                <a href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent("Hi! I'd like to learn more about Fitly India's fertility nutrition programs.")}`}
                  target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 border border-bone/20 text-bone px-8 py-3.5 text-sm tracking-wide hover:bg-bone/10 transition-all rounded-full">
                  <MessageCircle size={16} /> Message on WhatsApp
                </a>
              </div>
            </Anim>
          </div>
        </section>

        {/* ═══ MEDICAL DISCLAIMER ═══ */}
        <aside className="py-10 bg-bone border-t border-border_custom" data-testid="fn-disclaimer">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <p className="text-xs text-text_muted/70 leading-relaxed text-center">
              <strong className="text-text_muted">Medical Disclaimer:</strong> Information provided on this page is for educational purposes and does not constitute diagnosis or medical treatment. Fertility conditions require appropriate evaluation by qualified healthcare professionals. Nutrition and lifestyle interventions cannot guarantee conception or IVF success and should not replace medical treatment. Fitly India encourages clients to work with qualified medical professionals for clinical fertility care.
            </p>
          </div>
        </aside>
      </main>

      <LandingFooter />
    </div>
  );
}
